import { getDisplayName } from '@ps/web-core-utils';
import type { Error, ErrorOrPromise, Errors, Validators } from '@ps/web-shared-validators';
import type { FieldState, FieldValidator } from 'final-form';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { isFunction } from 'lodash';
import type { ComponentType } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface InjectedValidatorProps {
  validators?: Validators;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validator?: FieldValidator<any>;
  validationErrors?: Errors;
}

const getResult = (results: Error[]) => results.find((result) => !result.valid);

const withValidator = <P>(Component: React.ComponentType<P & InjectedValidatorProps>) => {
  const WithValidator: React.FunctionComponent<P & InjectedValidatorProps> = ({
    validators,
    ...props
  }) => {
    const [validationErrors, setValidationErrors] = useState<Errors>([]);
    const isMounted = useRef<boolean>(true);
    const getAsyncResult = useCallback(async (asyncResults: ErrorOrPromise[]) => {
      const results = await Promise.all<Error>(asyncResults);
      if (!isMounted.current) {
        return undefined;
      }

      setValidationErrors(results);

      return getResult(results);
    }, []);

    const combineValidators = () => {
      if (!validators) {
        return undefined;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (value: any, values: any, meta: FieldState<any>) => {
        const allResults = validators.map(
          (callback) => callback(value, values, meta) as Promise<Error>
        );
        const syncResults = allResults.filter((result: Promise<Error>) => !isFunction(result.then));

        setValidationErrors(syncResults as Errors);

        return allResults.length === syncResults.length
          ? getResult(allResults as Error[])
          : getAsyncResult(allResults);
      };
    };
    const validator = useMemo(combineValidators, [getAsyncResult, validators]);

    useEffect(
      () => () => {
        isMounted.current = false;
      },
      []
    );

    return React.createElement(Component, {
      ...props,
      validator,
      validationErrors
    } as P & InjectedValidatorProps);
  };

  WithValidator.displayName = `WithValidator(${getDisplayName(Component as ComponentType)})`;

  return hoistNonReactStatics(WithValidator, Component);
};

export default withValidator;
